import React, { useEffect, useState, useCallback } from "react";

// Helpers to derive card shades from the base color
function shade(hex, percent) {
  const h = hex.replace("#", "");
  const num = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round(percent * 255)));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(percent * 255)));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(percent * 255)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

const PRESETS = [
  { name: "Faded sage", color: "#a4b09c" },
  { name: "Soft moss", color: "#8e9c84" },
  { name: "Stone olive", color: "#9aa18b" },
  { name: "Eucalyptus", color: "#b4c1ab" },
  { name: "Greige", color: "#b3ad9a" },
  { name: "Dusty teal", color: "#8fa5a1" },
  { name: "Warm linen", color: "#c2b69e" },
  { name: "Original olive", color: "#7a8268" },
];

const KEY = "neurotheos_explore_bg";

function applyColor(hex) {
  const root = document.documentElement;
  root.style.setProperty("--explore-bg", hex);
  root.style.setProperty("--explore-card", shade(hex, -0.06));
  root.style.setProperty("--explore-card-border", shade(hex, -0.12));
}

export default function ExploreColorPicker() {
  const [open, setOpen] = useState(true);
  const [color, setColor] = useState("#a4b09c");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Read URL param + saved color
    const params = new URLSearchParams(window.location.search);
    setVisible(params.get("palette") === "1");
    const saved = localStorage.getItem(KEY);
    if (saved) {
      setColor(saved);
      applyColor(saved);
    }
  }, []);

  const change = useCallback((hex) => {
    setColor(hex);
    applyColor(hex);
    localStorage.setItem(KEY, hex);
  }, []);

  const reset = () => {
    localStorage.removeItem(KEY);
    const def = "#a4b09c";
    setColor(def);
    applyColor(def);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-[55] rounded-2xl shadow-2xl"
      style={{
        bottom: 20,
        left: 20,
        background: "#fbf8f1",
        border: "1px solid #e7e0cf",
        width: open ? 320 : 56,
        transition: "width .25s",
        overflow: "hidden",
      }}
      data-testid="palette-picker"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3"
        style={{ background: color, color: "#0e0e0e" }}
        data-testid="palette-toggle"
      >
        <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#fbf8f1", color: "#0e0e0e" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18" /></svg>
        </span>
        {open && <span className="text-xs uppercase tracking-widest" style={{ color: "#0e0e0e" }}>Explore color</span>}
      </button>

      {open && (
        <div className="p-4">
          <p className="text-xs mb-3" style={{ color: "var(--ink-muted)" }}>Pick a color or tap a preset — saves automatically.</p>

          <div className="flex items-center gap-3 mb-4">
            <input
              type="color"
              value={color}
              onChange={(e) => change(e.target.value)}
              data-testid="palette-input"
              style={{ width: 44, height: 44, borderRadius: 8, border: "1px solid #d3c8aa", cursor: "pointer", background: "transparent" }}
            />
            <div className="flex-1">
              <div className="font-mono text-sm" data-testid="palette-current">{color.toUpperCase()}</div>
              <div className="text-[10px]" style={{ color: "var(--ink-muted)" }}>Card shade auto-derived</div>
            </div>
            <button onClick={reset} className="text-[10px] uppercase tracking-widest" style={{ color: "var(--olive-deep)" }} data-testid="palette-reset">Reset</button>
          </div>

          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--ink-muted)" }}>Presets</div>
          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.color}
                onClick={() => change(p.color)}
                title={`${p.name} (${p.color})`}
                data-testid={`palette-preset-${p.color}`}
                aria-label={p.name}
                className="rounded-lg flex items-end justify-center pb-1"
                style={{
                  height: 56,
                  background: p.color,
                  border: color.toLowerCase() === p.color.toLowerCase() ? "2px solid #0e0e0e" : "1px solid #d3c8aa",
                  cursor: "pointer",
                }}
              >
                <span className="text-[9px] uppercase tracking-widest" style={{ color: "#fbf8f1", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{p.name.split(" ")[0]}</span>
              </button>
            ))}
          </div>

          <div className="mt-4 pt-3 border-t text-[10px]" style={{ borderColor: "var(--line)", color: "var(--ink-muted)" }}>
            Tip: remove <code>?palette=1</code> from URL to hide this. Tell me your chosen color and I'll lock it in.
          </div>
        </div>
      )}
    </div>
  );
}
